import React from 'react';
import { Award, Users, Target, Heart } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="bg-[#F4EFE8] min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] lg:h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBraXRjaGVufGVufDB8fHx8MTc3MTIzODA2NXww&ixlib=rb-4.1.0&q=85"
            alt="About Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#111111]/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            About NorthFold
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Where craftsmanship meets contemporary design.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl text-[#111111] mb-8">
              Our Story
            </h2>
            <div className="space-y-6 text-[#242424] text-lg leading-relaxed">
              <p>
                NorthFold was born from a simple belief: every home deserves spaces that inspire daily living. Based in Jalandhar, we've grown from a small workshop into a trusted name for premium modular kitchens and wardrobes across the region.
              </p>
              <p>
                What sets us apart is our commitment to precision engineering and the finest materials. Whether you're seeking luxury bespoke designs or smart budget solutions, we bring the same level of care, craftsmanship, and attention to detail to every project.
              </p>
              <p>
                Our team combines traditional woodworking expertise with modern manufacturing technology. We don't just build kitchens and wardrobes—we create functional art that enhances your daily life and adds lasting value to your home.
              </p>
            </div>
          </div>

          <div className="border-l-4 border-[#B89A4F] pl-8 py-6 bg-white/50">
            <p className="text-2xl font-serif text-[#111111] italic">
              "Designed for Living. Crafted for Life."
            </p>
            <p className="text-[#242424] mt-4">
              This isn't just our tagline—it's our promise. We create spaces that grow with you, built to last generations.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl text-[#111111] mb-4">
              Our Values
            </h2>
            <p className="text-[#242424] max-w-2xl mx-auto leading-relaxed">
              The principles that guide every decision and every detail.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B89A4F]/10 rounded-full mb-6">
                <Award className="text-[#B89A4F]" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-[#111111] mb-3">Excellence</h3>
              <p className="text-[#242424] leading-relaxed">
                Uncompromising quality in materials, craftsmanship, and service delivery.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B89A4F]/10 rounded-full mb-6">
                <Users className="text-[#B89A4F]" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-[#111111] mb-3">Partnership</h3>
              <p className="text-[#242424] leading-relaxed">
                We work closely with clients to understand and realize their vision.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B89A4F]/10 rounded-full mb-6">
                <Target className="text-[#B89A4F]" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-[#111111] mb-3">Precision</h3>
              <p className="text-[#242424] leading-relaxed">
                Every measurement, every cut, every installation executed with accuracy.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B89A4F]/10 rounded-full mb-6">
                <Heart className="text-[#B89A4F]" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-[#111111] mb-3">Care</h3>
              <p className="text-[#242424] leading-relaxed">
                Treating every home and every project with respect and dedication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#111111] mb-8">
                Why Choose NorthFold?
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: '35+ Years of Experience',
                    desc: 'Precision interior craftsmanship, serving premium homes across the region.'
                  },
                  {
                    title: 'Premium Materials Only',
                    desc: 'Marine plywood, HDHMR boards, German hardware, and luxury finishes.'
                  },
                  {
                    title: 'In-house Manufacturing',
                    desc: 'Complete control over quality with our advanced production facility.'
                  },
                  {
                    title: 'End-to-End Service',
                    desc: 'From design consultation to final installation, we handle everything.'
                  },
                  {
                    title: 'Timely Execution',
                    desc: 'We respect your time and deliver projects as promised.'
                  },
                  {
                    title: 'Factory Warranty',
                    desc: 'Comprehensive warranty coverage on all products and installations.'
                  }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-4">
                    <div className="w-2 h-2 bg-[#B89A4F] rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#111111] mb-1">{item.title}</h3>
                      <p className="text-[#242424]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="aspect-[3/4] overflow-hidden bg-white shadow-md">
                <img
                  src="https://images.unsplash.com/photo-1611095210561-67f0832b1ca3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBraXRjaGVufGVufDB8fHx8MTc3MTIzODA2NXww&ixlib=rb-4.1.0&q=85"
                  alt="Quality"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="aspect-[3/4] overflow-hidden bg-white shadow-md mt-12">
                <img
                  src="https://images.unsplash.com/photo-1672137233327-37b0c1049e77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3YXJkcm9iZXxlbnwwfHx8fDE3NzEyMzgxMDJ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Craftsmanship"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-[#111111] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl lg:text-5xl mb-6">
            Let's Build Something Beautiful
          </h2>
          <p className="text-lg text-white/80 mb-12 leading-relaxed">
            Ready to transform your space? Get in touch with our team today.
          </p>
          <a
            href="/contact"
            className="inline-block bg-[#B89A4F] text-white px-12 py-5 text-sm tracking-widest uppercase hover:bg-white hover:text-[#111111] transition-all duration-300"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
